import React, { useMemo } from 'react';
import { ContentContainerType } from 'types/enums';
import { Link } from 'types/api.types';
import { useAppDispatch, useAppSelector } from 'app/store';
import { useFetch, useResource } from 'app/utils/hooks';
import { Connection, CreateConnectionResponse } from 'types/profile';
import {
  getConnections,
  getCreateConnectionLink,
} from 'app/store/Profile/selectors';
import {
  deleteConnectionEffect,
  fetchConnectionsEffect,
} from 'app/store/Profile/effects';
import { Navigate } from 'react-router-dom';
import {
  connectToSoundcloudAttemptAction,
  connectToTuneCoreAttemptAction,
} from 'app/store/Mixpanel/slice';
import { routes } from 'app/routing/routesConfig';
import { openURL } from 'app/utils/utils';
import { ChakraAppLoader } from 'app/components/Core';
import { SOUNDCLOUD_PROVIDER_ID, TUNECORE_PROVIDER_ID } from 'app/constants';
import { ContentContainer } from 'app/components/Core/ChakraContentContainer';
import { getCreateReleaseValidations } from 'app/store/Releases/selectors';
import { getAccountConnectionProviders } from 'app/store/Auth/selectors';
import { useHandleConnectionUrlPayload } from './hooks';
import { ConnectionsListComponent } from './ConnectionsListComponent';

const initialSoundCloudConnection = {
  providerId: SOUNDCLOUD_PROVIDER_ID,
} as Connection;

const initialTuneCoreConnection = {
  providerId: TUNECORE_PROVIDER_ID,
} as Connection;

export const ConnectionsList = () => {
  const dispatch = useAppDispatch();
  const accountConnectionProviders = useAppSelector(
    getAccountConnectionProviders
  );
  const releaseValidations = useAppSelector(getCreateReleaseValidations);
  const showConnectionsList =
    releaseValidations && releaseValidations?.type?.enum?.length > 1;
  const createConnectionLink = useAppSelector(getCreateConnectionLink);
  const [connections, scConnectionLoading] = useResource(
    fetchConnectionsEffect,
    getConnections,
    { useCache: false }
  );

  const allowedConnections = useMemo(
    () => accountConnectionProviders?.map(({ providerId }) => providerId) || [],
    [accountConnectionProviders]
  );

  useHandleConnectionUrlPayload();

  const connect = useFetch<{}, CreateConnectionResponse>();

  const handleOnConnect = async (providerId: string) => {
    if (createConnectionLink) {
      const { response } = await connect.callAPI(createConnectionLink.href, {
        providerId,
      });

      if (providerId === 'SoundCloud') {
        dispatch(connectToSoundcloudAttemptAction());
      }
      if (providerId === 'TuneCore') {
        dispatch(connectToTuneCoreAttemptAction({ page: 'Profile' }));
      }

      openURL(response.accountConnection._links.redirect.href, {
        isExternal: true,
      });
    }
  };

  const handleOnDisconnect = async (
    disconnectLink: Link,
    guid: string,
    name: string,
    providerId
  ) => {
    await dispatch(
      deleteConnectionEffect({
        providerId,
        connectionGuid: guid,
        deleteLink: disconnectLink,
        name,
      })
    );
  };

  const connectionsList = useMemo(() => {
    const scConnection = connections.find(
      ({ providerId }) => providerId === 'SoundCloud'
    );
    const tcConnection = connections.find(
      ({ providerId }) => providerId === 'TuneCore'
    );

    return [
      scConnection || initialSoundCloudConnection,
      ...(allowedConnections.includes('TuneCore')
        ? [tcConnection || initialTuneCoreConnection]
        : []),
    ];
  }, [connections, allowedConnections]);

  if (!releaseValidations) {
    return <ChakraAppLoader />;
  }

  if (!showConnectionsList) {
    return <Navigate to={routes.profileBasic.path} />;
  }

  return (
    <ContentContainer type={ContentContainerType.LIST} pt={4}>
      <ConnectionsListComponent
        onConnect={handleOnConnect}
        onDisconnect={handleOnDisconnect}
        loading={scConnectionLoading}
        connections={connectionsList}
      />
    </ContentContainer>
  );
};
